import {
	EventPriceDto,
	EventPriceTypesEnum,
	EventTicketsReservationDto,
	EventTicketsTotalsWithReservationsDto,
	isPriceSoldOut,
} from '@sweatpals/shared';
import { isNumber } from 'lodash-es';
import { useMemo } from 'react';
import { isBefore } from 'date-fns';

export interface PriceTierDataProps {
	priceTier: EventPriceDto;
	// price tier selected quantity before reservation
	selectedQuantity?: number;
	// event free spots minus all selected quantity
	freeSpotsCount?: number;
	eventTicketsCountAndSpots?: EventTicketsTotalsWithReservationsDto;
	userTicketReservations?: EventTicketsReservationDto[];
}

export const usePriceTierData = (props: PriceTierDataProps): {
	isTierSoldOut: boolean;
	isOutOfTickets: boolean;
	availableTickets: number;
} => {
	const {
		priceTier,
		selectedQuantity,
		freeSpotsCount,
		eventTicketsCountAndSpots,
		userTicketReservations,
	} = props;
	const {
		basePriceId,
		maxTickets,
		maxTicketsPerOrder,
	} = priceTier ?? {};

	const ticketsPerGroup = useMemo(() => (priceTier?.ticketsPerGroup ?? 1), [priceTier?.ticketsPerGroup]);
	const ticketsCount = useMemo(() => (priceTier?.ticketsCount ?? 0), [priceTier?.ticketsCount]);
	// console.group('usePriceTierData-props');
	// console.debug('freeSpotsCount', freeSpotsCount);
	// console.debug('priceTierSpotsCount', priceTierSpotsCount);
	// console.debug('selectedQuantity', selectedQuantity);
	// console.debug('ticketsCount', ticketsCount);
	// console.debug('maxTickets', maxTickets);
	// console.groupEnd();

	// TODO - fix price tier spots count

	const isTierSoldOut = useMemo(
		() => isPriceSoldOut(priceTier, eventTicketsCountAndSpots, userTicketReservations),
		[eventTicketsCountAndSpots, priceTier, userTicketReservations]
	);

	const availableTickets = useMemo(() => {
		if (!isNumber(maxTickets)) {
			return;
		}

		const validTicketReservations = (userTicketReservations ?? []).filter(({ expiresAt }) => isBefore(new Date(), expiresAt));
		const reservedPriceTicketsCount = validTicketReservations.filter(r => r.baseEventPriceId === basePriceId).length;
		const priceSpots = eventTicketsCountAndSpots?.pricesSpots?.find(p => p.basePriceId === basePriceId);

		return priceSpots
			? priceSpots.spotsCountWithReservations + reservedPriceTicketsCount
			: maxTickets - ticketsCount;
	}, [maxTickets, userTicketReservations, eventTicketsCountAndSpots, ticketsCount, basePriceId]);

	const isOutOfTickets = useMemo(() => {
		if (!isNumber(maxTickets) && !isNumber(freeSpotsCount) && !isNumber(maxTicketsPerOrder)) {
			return false;
		}

		return (isNumber(freeSpotsCount) && freeSpotsCount < ticketsPerGroup)
			|| (isNumber(maxTickets) && (ticketsCount + selectedQuantity + ticketsPerGroup) > maxTickets)
			|| (isNumber(maxTicketsPerOrder) && (selectedQuantity + ticketsPerGroup) > maxTicketsPerOrder)
	}, [maxTickets, freeSpotsCount, ticketsPerGroup, ticketsCount, selectedQuantity, maxTicketsPerOrder]);

	// console.group('usePriceTierData.results');
	// console.debug('isTierSoldOut', isTierSoldOut);
	// console.debug('isOutOfTickets', isOutOfTickets);
	// console.debug('ticketsAvailable', ticketsAvailable);
	// console.groupEnd();

	return {
		isTierSoldOut,
		isOutOfTickets,
		availableTickets,
	};
};
